// app.component.ts (refactored)
import {
  Component,
  inject,
  HostListener,
  AfterViewInit,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CookieBar } from './components/cookie-bar/cookie-bar';
import { CookieSettings } from './components/cookie-settings/cookie-settings';
import {
  CookieConsentService,
  CookieConsent,
} from './services/cookie-consent.service';
import { PRIVACY_CHOICES_URL } from './data/structured-data';
import { url } from 'inspector';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterModule, CookieBar, CookieSettings],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class App implements OnInit, AfterViewInit {
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private cookieConsentService = inject(CookieConsentService);

  isMobileMenuOpened = false;
  isScrolled = false;
  isCookieModalOpen = false;
  isCookiesBarVisible = false;

  year = new Date().getFullYear();

  prompt =
    'Explain what MARI Group (marigrp.com) is and what they do. MARI is a global live events and experiences company founded by Ariel Emanuel, with Mark Shapiro as Principal Investor and Board Member. Describe their portfolio across sports (Miami Open, Mutua Madrid Open, Nike Melbourne Marathon), arts (Frieze London, Frieze Masters, Frieze Abu Dhabi, Frieze Seoul), lifestyle (Hyde Park Winter Wonderland), collectibles (Barrett-Jackson auctions), and ticketing technology (TodayTix, Bucket Listers). Explain the audience profile for someone considering partnering with or attending MARI events, and outline the key benefits of engaging with MARI as a sponsor, partner, or attendee. Highlight how MARI is reshaping the live events landscape globally.';

  headerLinks = [
    { title: 'About', url: '/about' },
    { title: 'Portfolio', url: '/portfolio' },
    { title: 'Press', url: '/press' },
    { title: 'Contact', url: '/contact' },
  ];

  footerLinks = [
    { title: 'About', url: 'about' },
    { title: 'Portfolio', url: 'portfolio' },
    { title: 'Press', url: 'press' },
    {
      title: 'Careers',
      url: 'https://mari.careers.hibob.com/',
      isExternal: true,
    },
    { title: 'Contact', url: 'contact' },
  ];

  serviceLinks = [
    { title: 'SUPPLIER CODE OF CONDUCT', url: 'supplier-code-of-conduct' },
    { title: 'TERMS OF USE', url: 'terms-of-use' },
    { title: 'PRIVACY', url: 'privacy' },
    {
      title: 'YOUR PRIVACY CHOICES',
      url: PRIVACY_CHOICES_URL,
      isExternal: true,
    },
    {
      title: 'COOKIE SETTINGS',
      url: undefined,
      action: () => this.onCookieModalOpen(),
    },
  ];

  socialLinks = [
    {
      title: 'Linkedin',
      url: 'https://www.linkedin.com/company/mari-grp',
      icon: 'icon-linkedin.svg',
    },
    {
      title: 'Instagram',
      url: 'https://www.instagram.com/themarigroup',
      icon: 'icon-instagram.svg',
    },
    { title: 'X', url: 'https://x.com/themarigroup', icon: 'icon-x.svg' },
  ];

  private readonly isMainPage$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map((event: NavigationEnd) => {
      return event.url.split('#')[0] === '/' || event.url.includes('/about');
    }),
    startWith(true),
  );

  isMainPage = toSignal(this.isMainPage$);

  private readonly isMenuPage$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map((event: NavigationEnd) => {
      const url = (event as any).urlAfterRedirects ?? event.url;
      return this.headerLinks.some((p) => url.startsWith(p.url));
    }),
    startWith(this.headerLinks.some((p) => this.router.url?.startsWith(p.url))),
  );

  isMenuPage = toSignal(this.isMenuPage$);

  get isMenuPageValue() {
    return this.isMenuPage();
  }

  constructor() {
    this.cookieConsentService.openSettings$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.onCookieModalOpen());
  }

  ngOnInit(): void {
    // Check if user has already given consent
    if (!this.cookieConsentService.hasConsent()) {
      this.isCookiesBarVisible = true;
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.setScrolled());
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (isPlatformBrowser(this.platformId)) {
      this.setScrolled();
    }
  }

  onCookieModalOpen(): void {
    this.isCookieModalOpen = true;
    this.toggleBodyScroll(false);
  }

  onCookieModalClose(preferences: CookieConsent | null): void {
    this.isCookieModalOpen = false;
    this.toggleBodyScroll(true);

    if (preferences !== null) {
      this.setCookiesPreferences(preferences);
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpened = !this.isMobileMenuOpened;
  }

  closeMobileMenu() {
    if (this.isMobileMenuOpened) {
      this.isMobileMenuOpened = false;
    }
  }

  setCookiesPreferences(preferences: CookieConsent): void {
    if (!preferences) {
      return;
    }

    this.isCookiesBarVisible = false;
    this.cookieConsentService.saveConsent(preferences);
  }

  private setScrolled() {
    this.isScrolled = window.scrollY > 1;
  }

  private toggleBodyScroll(enable: boolean): void {
    if (enable) {
      document.body.style.overflow = '';
    } else {
      document.body.style.overflow = 'hidden';
    }
  }
}
